/**
 * @module Components.SignUp
 *
 * Names the signup form's fields and errors the way the funnel events
 * report them (CN-2742): First Field on Signup Form Started, and the
 * Field / Error Type pairs of Signup Field Error.
 */
import { MessageDescriptor } from "react-intl";
import { UserModel } from "app/models";

export type FieldError = { field: string; type: string };

// Validation error keys, in form order.
const FIELD_NAMES: { [key: string]: string } = {
  firstName: "first_name",
  lastName: "last_name",
  email: "email",
  password: "password",
  tosConsent: "tos_consent",
  kind: "kind",
};

const INPUT_NAMES: { [key: string]: string } = {
  ...FIELD_NAMES,
  passwordVerify: "password_confirmation",
  ageConsent: "age_consent",
  phone: "phone",
};

/** The field name for a form input's name attribute; other for the rest. */
export const formFieldName = (inputName: string): string =>
  INPUT_NAMES[inputName] || "other";

const passwordLengthType = (user: Partial<UserModel>) =>
  (user.password || "").length < 6 ? "password_too_short" : "password_too_long";

// Keyed by the locale ids validate.ts reports. Any other message on a field
// is its required-field message.
const typeFor = (
  message: string | MessageDescriptor,
  user: Partial<UserModel>,
): string => {
  const id = typeof message === "string" ? message : message.id;
  switch (id) {
    case "error.invalid.email":
      return "invalid_email";
    case "error.invalid.password":
      return passwordLengthType(user);
    case "error.mismatch.password":
      return "password_mismatch";
    default:
      return "required";
  }
};

/** One entry per failing field in validateUser()'s errors, in form order. */
export const validationFieldErrors = (
  errors: { [key: string]: string | MessageDescriptor | null | undefined },
  user: Partial<UserModel>,
): FieldError[] =>
  Object.keys(FIELD_NAMES)
    .filter((key) => errors[key] != null)
    .map((key) => ({ field: FIELD_NAMES[key], type: typeFor(errors[key], user) }));

const API_FIELD_ERRORS: { [key: string]: FieldError } = {
  email_unavailable: { field: "email", type: "email_taken" },
  duplicate_email: { field: "email", type: "email_taken" },
  email_already_taken: { field: "email", type: "email_taken" },
  email_invalid: { field: "email", type: "invalid_email" },
  phone_already_exists: { field: "phone", type: "phone_taken" },
  phone_already_taken: { field: "phone", type: "phone_taken" },
};

/**
 * The field errors among the error keys POST account answered with. The
 * password length error says too short or too long by what was typed.
 */
export const apiFieldErrors = (
  keys: string[] | undefined,
  user: Partial<UserModel> = {},
): FieldError[] =>
  (keys || [])
    .map((key) =>
      key === "password_length"
        ? { field: "password", type: passwordLengthType(user) }
        : API_FIELD_ERRORS[key],
    )
    .filter(Boolean);
